import React from "react";

export default function Modal({ title, onClose, children, width, onSubmit, submitLabel, submitDisabled }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.5)", display: "flex",
        alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: 24,
          width: width || 480, maxHeight: "85vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 20,
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1e293b" }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", fontSize: 20,
              cursor: "pointer", color: "#94a3b8", padding: "0 4px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        {children}

        {/* Footer — rendered when onSubmit is provided */}
        {onSubmit && (
          <div style={{
            display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20,
            paddingTop: 16, borderTop: "1px solid #e2e8f0",
          }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 20px", borderRadius: 6,
                border: "1px solid #d1d5db", background: "#fff",
                color: "#475569", fontSize: 13, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={submitDisabled}
              style={{
                padding: "10px 24px", borderRadius: 6, border: "none",
                background: submitDisabled ? "#94a3b8" : "#2563eb",
                color: "#fff", fontSize: 13, fontWeight: 600,
                cursor: submitDisabled ? "not-allowed" : "pointer",
                opacity: submitDisabled ? 0.7 : 1,
              }}
            >
              {submitLabel || "Submit"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function FormField({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: "block", fontSize: 12, fontWeight: 600,
        color: "#475569", marginBottom: 4,
      }}>
        {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export function Input({ type, value, onChange, placeholder, required, step, min }) {
  const [focused, setFocused] = React.useState(false);
  const _s = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, outline: "none", boxSizing: "border-box" };

  if (type === "number" || type === "money") {
    const fmt = (v) => {
      if (v === "" || v == null) return "";
      const n = parseFloat(String(v).replace(/,/g, ""));
      if (isNaN(n)) return String(v);
      const parts = String(v).split(".");
      const decs = parts.length > 1 ? Math.min(parts[1].length, 4) : 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: decs, maximumFractionDigits: 4 });
    };
    return (
      <input type="text" inputMode="decimal" style={_s}
        value={focused ? (value ?? "") : fmt(value)}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.\-]/g, ""))}
        placeholder={placeholder} required={required} />
    );
  }

  if (type === "pct") {
    return (
      <div style={{ position: "relative" }}>
        <input type="text" inputMode="decimal" style={{ ..._s, paddingRight: 28 }}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.\-]/g, ""))}
          placeholder={placeholder || "0"} required={required} />
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 13, pointerEvents: "none" }}>%</span>
      </div>
    );
  }

  return (
    <input
      type={type || "text"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      step={step}
      min={min}
      style={_s}
    />
  );
}

const _mIS = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, outline: "none", boxSizing: "border-box" };

export function MoneyInput({ value, onChange, placeholder, style }) {
  const [focused, setFocused] = React.useState(false);
  const fmt = (v) => {
    if (v === "" || v == null) return "";
    const n = parseFloat(String(v).replace(/,/g, ""));
    if (isNaN(n)) return String(v);
    const parts = String(v).split(".");
    const decimals = parts.length > 1 ? Math.min(parts[1].length, 4) : 0;
    return n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: 4 });
  };
  const raw = (v) => v.replace(/[^0-9.\-]/g, "");
  return (
    <input type="text" inputMode="decimal" style={{ ..._mIS, ...style }}
      value={focused ? (value ?? "") : fmt(value)}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      onChange={(e) => onChange(raw(e.target.value))} placeholder={placeholder || "0"} />
  );
}

export function PctInput({ value, onChange, placeholder, style }) {
  return (
    <div style={{ position: "relative", ...style }}>
      <input type="text" inputMode="decimal"
        style={{ ..._mIS, paddingRight: 28 }}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.\-]/g, ""))}
        placeholder={placeholder || "0"} />
      <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 13, pointerEvents: "none" }}>%</span>
    </div>
  );
}

export function Select({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%", padding: "8px 10px", borderRadius: 6,
        border: "1px solid #d1d5db", fontSize: 13, outline: "none",
        boxSizing: "border-box", background: "#fff",
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export function TextArea({ value, onChange, placeholder, rows }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows || 3}
      style={{
        width: "100%", padding: "8px 10px", borderRadius: 6,
        border: "1px solid #d1d5db", fontSize: 13, outline: "none",
        boxSizing: "border-box", resize: "vertical", fontFamily: "inherit",
      }}
    />
  );
}

export function FormRow({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      {children}
    </div>
  );
}

export function SubmitButton({ onClick, loading, label }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
      <button
        onClick={onClick}
        disabled={loading}
        style={{
          padding: "10px 24px", borderRadius: 6, border: "none",
          background: "#2563eb", color: "#fff", fontSize: 13,
          fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? "Saving..." : label || "Save"}
      </button>
    </div>
  );
}

export function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div style={{
      background: "#fee2e2", color: "#991b1b", padding: "8px 12px",
      borderRadius: 6, fontSize: 13, marginBottom: 14,
    }}>
      {message}
    </div>
  );
}
